import './assets/css/App.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import AuthLayout from './layouts/auth';
import AdminLayout from './layouts/admin';
import RTLLayout from './layouts/rtl';
import { ChakraProvider } from '@chakra-ui/react';
import initialTheme from './theme/theme';
import { useState, useEffect } from 'react';
import { getUserByFirebaseUid } from './api/api';
import { useFirebaseAuth } from 'domains/auth/hooks/useFirebaseAuth';
import { FleetProvider } from 'contexts/FleetContext';
import { ListingProvider } from 'contexts/ListingContext';
import { getUserVerificationStatus } from './services/auth';
import { getAuth } from 'firebase/auth';
import API from './api/api';
import EmailVerification from 'domains/auth/pages/verification/EmailVerification';
import VerificationPendingScreen from 'domains/auth/pages/verification/VerificationPendingScreen';
import LoadingScreen from 'components/loading/LoadingScreen';
import {
  clearPendingSignupRolePreference,
  getPendingSignupRolePreference,
} from 'domains/auth/utils/registrationRole';
import { getPostAuthRedirectPath } from 'domains/auth/utils/postAuthRedirect';

const Root = () => {
  const { uid, loading } = useFirebaseAuth();
  const [isLoadingRole, setIsLoadingRole] = useState(true);
  const [redirectPath, setRedirectPath] = useState(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (uid) {
        try {
          await getUserVerificationStatus(uid);
          const user = await getUserByFirebaseUid(uid);
          setRedirectPath(getPostAuthRedirectPath(user));
        } catch (error) {
          // If user doesn't exist in database yet (during signup or incomplete registration)
          if (error.response?.data?.code === 'USER_NOT_IN_DATABASE') {
            console.log('User not in database yet.');
            
            // Do not auto-recover if we are currently on the an auth page (like sign-in or sign-up)
            if (window.location.pathname.includes('/auth/')) {
               console.log('User is in auth flow, skipping auto-recovery.');
               setRedirectPath(null); // Let the auth flow finish
               setIsLoadingRole(false);
               return;
            }

            console.log('Attempting auto-recovery...');
            // AUTO-RECOVERY: Complete registration automatically
            try {
              const auth = getAuth();
              const currentUser = auth.currentUser;

              if (currentUser) {
                // Create MongoDB user with safe default role
                const token = await currentUser.getIdToken();
                const rolePreference =
                  getPendingSignupRolePreference(currentUser.email) || 'client';
                await API.post(
                  '/users',
                  {
                    uid: currentUser.uid,
                    email: currentUser.email,
                    displayName: currentUser.displayName || currentUser.email,
                    rolePreference,
                  },
                  {
                    headers: { Authorization: `Bearer ${token}` },
                  },
                );

                clearPendingSignupRolePreference();
                console.log(
                  'Auto-recovery successful - user created in database',
                );

                // Retry fetching user status
                const user = await getUserByFirebaseUid(currentUser.uid);
                setRedirectPath(getPostAuthRedirectPath(user));
                setIsLoadingRole(false);
                return;
              }
            } catch (recoveryError) {
              console.error('Auto-recovery failed:', recoveryError);
              // If auto-recovery fails, redirect to signup to complete manually
              setRedirectPath('/auth/sign-in');
              setIsLoadingRole(false);
              return;
            }
          }

          if (error.response?.data?.code === 'USER_NOT_APPROVED') {
             setRedirectPath('/verification/pending');
             setIsLoadingRole(false);
             return;
          }

          if (error.response?.data?.code === 'USER_EMAIL_NOT_VERIFIED') {
             setRedirectPath('/email-verification');
             setIsLoadingRole(false);
             return;
          }

          console.error('Failed to fetch user role:', error);
          // Sign out of Firebase to break the infinite redirect loop with AuthLayout
          try {
            const auth = getAuth();
            if (auth.currentUser) await import('firebase/auth').then(m => m.signOut(auth));
          } catch (signOutError) {
             console.error('Error signing out during safeguard:', signOutError);
          }
          setRedirectPath('/auth/sign-in');
        }
      } else {
        setRedirectPath('/auth/sign-in');
      }
      setIsLoadingRole(false);
    };

    if (!loading) {
      fetchUserRole();
    }
  }, [uid, loading]);

  if (loading || isLoadingRole) {
    return <LoadingScreen />;
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  // Fallback to client dashboard if no redirect path is set but we finished loading
  return <Navigate to="/dashboard/client" replace />;
};

export default function Main() {
  const [currentTheme, setCurrentTheme] = useState(initialTheme);

  return (
    <ChakraProvider theme={currentTheme}>
      <Routes>
        <Route path="auth/*" element={<AuthLayout />} />
        <Route path="/email-verification" element={<EmailVerification />} />
        <Route path="/verification/pending" element={<VerificationPendingScreen />} />
        <Route
          path="/*"
          element={
            <FleetProvider>
              <ListingProvider>
                <AdminLayout
                  theme={currentTheme}
                  setTheme={setCurrentTheme}
                />
              </ListingProvider>
            </FleetProvider>
          }
        />

        <Route
          path="rtl/*"
          element={
            <RTLLayout theme={currentTheme} setTheme={setCurrentTheme} />
          }
        />
        <Route path="/" element={<Root />} />
      </Routes>
    </ChakraProvider>
  );
}
